/**
 * Edit-existing-PCO modal — now the single place a PM manages a PCO's
 * funding (funding buckets, findings #13 + #8).
 *
 * Mounted from the PCO command center's "Edit details" button. Replaces the
 * old "Amount proposed" + free 6-value Status controls with:
 *   - a Funding buckets list editor (each bucket == one funding_tranches
 *     row: amount + Pending/Approved + MOD/FWA/date/comments), and
 *   - a dedicated Void toggle (paid/reopen stay on the header button).
 *
 * Renders as four white .pco-card sections, top to bottom: PCO details
 * (PCO #, Description, Billing method) — Funding buckets — Comments —
 * Status (the no-cost toggle + the Void control). Jump to the matching
 * comment in the JSX below rather than scanning for it.
 *
 * A bucket's Pending/Funded state is STORED (`funding_tranches.is_funded`,
 * client ruling 2026-08-06). The segmented control writes that flag and
 * nothing else — MOD / FWA / funded date are ordinary fields that persist in
 * either state, because the GC issues those references before the funding
 * executes. Flipping a bucket to Pending used to null all three on save and
 * warn about it; both are gone, so a mis-click costs nothing.
 *
 * Seeding + the lazy per-PCO retirement of `amount_proposed_cents` are the
 * backward-compat bridge for existing PCOs — see `seedRows` and `submit`. The
 * two halves have to agree about every PCO: the retirement nulls the legacy
 * figure only where the seed migrated it into a bucket, which a DEDUCT (and an
 * unloaded figure) is not. Both decisions live in `lib/pcoMoney.ts` —
 * `legacyProposedResidualCents` and `proposedRetirementPatch` — because a
 * disagreement between them deletes money (PCO 063, prod, 2026-08-06).
 */
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatMoneyCents, formatPcoNumber, gcName } from '../lib/format';
import {
  legacyProposedResidualCents,
  proposedRetirementPatch,
  trancheIsFunded,
} from '../lib/pcoMoney';
import { HelpTip } from './HelpTip';
import { useToast } from './ToastProvider';
import { Dropdown, Toggle } from './design';
import {
  BucketRowEditor,
  bodyForRow,
  blankPendingRow,
  DATE_RE,
  isBlankNewRow,
  parseAmountCents,
  type BucketRow,
} from './pco/BucketRowEditor';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import type {
  FundingTranche,
  FundingTrancheWriteRequest,
  PcoBillingType,
  PcoStatus,
  PcoWriteRequest,
  SettingsPco,
} from '@coffman-tm/shared';

export interface EditPcoModalInitial {
  id: string;
  pco_number: string;
  description: string;
  status: PcoStatus;
  /**
   * null while the settings-PCO list (the only payload carrying it) is still
   * loading. The modal disables the control and OMITS the field on save in
   * that case, so the server keeps the stored value — a guess here would
   * silently misfile the PCO.
   */
  billing_type: PcoBillingType | null;
  /**
   * The PCO's STORED legacy proposed figure — what the save's retirement
   * decides about, and what the seed's residual is derived from. It rides the
   * same settings-PCO payload as `billing_type`, so it has the same three
   * states: a number, a real `null` (already retired), and `undefined` for
   * "that list hasn't loaded". They are NOT interchangeable — the modal omits
   * the field on save while it is `undefined` rather than nulling a figure it
   * never read.
   */
  amount_proposed_cents: number | null | undefined;
  comments: string | null;
  void_reason: string | null;
  is_no_cost: boolean;
}

const centsToInput = (cents: number) => (cents / 100).toFixed(2);

const BILLING_TYPE_OPTIONS = [
  { value: 'weekly_rollup', label: 'T&M Weekly Rollup' },
  { value: 'lump_sum', label: 'Lump-Sum' },
];

/**
 * Seed the editor from the PCO's tranches + legacy proposed amount. This is
 * the backward-compat bridge (spec Q2): each tranche's stored `is_funded`
 * picks its bucket status; the residual of a legacy proposed amount over the
 * funded sum becomes a leading Pending bucket, so an untouched PCO's
 * displayed Approved / Pending figures reproduce exactly.
 *
 * Exported for its test: what this function does or doesn't seed is half of
 * whether the save's retirement is safe.
 */
export function seedRows(
  tranches: FundingTranche[],
  amountProposedCents: number | null | undefined,
): BucketRow[] {
  const rows: BucketRow[] = tranches.map((t) => {
    const approved = trancheIsFunded(t);
    return {
      key: t.id,
      trancheId: t.id,
      amountDollars: centsToInput(t.approved_amount_cents),
      approved,
      mod: t.mod_number ?? '',
      fwa: t.hoffman_fwa ?? '',
      approvedDate: t.approved_date ?? '',
      comments: t.comments ?? '',
      // Existing Approved buckets rest collapsed (their summary is enough);
      // Pending buckets open expanded so they invite completion.
      expanded: !approved,
    };
  });

  const approvedSum = tranches
    .filter(trancheIsFunded)
    .reduce((a, t) => a + t.approved_amount_cents, 0);
  // `max(0, proposed − approved)`, and 0 whenever there is no proposal to
  // gap-fill. Never inline this: read as `(proposed ?? 0) − approvedSum` it
  // turns a negative FUNDED bucket into phantom positive pending.
  const residual = legacyProposedResidualCents(amountProposedCents, approvedSum);
  if (residual > 0) {
    rows.unshift({ ...blankPendingRow(), amountDollars: centsToInput(residual) });
  }

  // Brand-new-style PCO (no tranches, no proposed): start with one blank
  // Pending bucket so there's always something to fill in.
  if (tranches.length === 0 && amountProposedCents == null) {
    rows.push(blankPendingRow());
  }
  return rows;
}

/**
 * Exported for its test — "the save leaves an untouched bucket alone" is an
 * assertion about this predicate, not about the network.
 */
export function trancheChanged(orig: FundingTranche, b: FundingTrancheWriteRequest): boolean {
  return (
    orig.approved_amount_cents !== b.approved_amount_cents ||
    (orig.mod_number ?? null) !== (b.mod_number ?? null) ||
    (orig.hoffman_fwa ?? null) !== (b.hoffman_fwa ?? null) ||
    (orig.approved_date ?? null) !== (b.approved_date ?? null) ||
    // The status is its own stored field now — a Pending/Funded flip with no
    // other edit still has to reach the server. The LHS goes through the
    // helper, never the raw column: on a response predating `is_funded` the
    // raw value is undefined and every untouched bucket would look changed,
    // firing a PATCH per bucket on every save. The RHS needs no such guard —
    // `bodyForRow` is the only producer of `b` and always sets the field.
    trancheIsFunded(orig) !== b.is_funded ||
    (orig.comments ?? null) !== (b.comments ?? null)
  );
}

/**
 * The "064 silence" incident (live review, 2026-08-06): a PM edited a
 * bucket's amount and typed a MOD, didn't flip it to Funded, and saved.
 * Everything persisted correctly as an ordinary Pending-with-a-reference
 * bucket (client ruling 2026-08-06 — the GC issues refs before the money
 * executes) — but nothing on screen SAID so, and the header's Approved-led
 * funding card showed a dash for the same PCO. Read together, that looked
 * like the save had eaten the PM's edit.
 *
 * STATE, NOT TRANSITION — the deliberate boundary, named the way
 * `trancheIsFunded`'s docstring names its own. This fires on every save that
 * LEAVES a bucket Pending while it carries a MOD, FWA, or funded date,
 * whether or not THIS save is what put the reference there — it is not
 * diff-gated against what changed. A PCO can sit on such a bucket across many
 * unrelated saves (a description tweak, a comment), and this says so every
 * time. That is the reviewer's ruling, not an oversight: a standing reminder
 * beats a one-time notification, and gating on a diff would silently
 * reintroduce the 064 silence the very next time that same PCO gets saved for
 * an unrelated reason. Funded buckets and a bare Pending bucket (no MOD, no
 * FWA, no funded date) are unremarkable and produce nothing, every time.
 *
 * Reads the rows `submit()` just finished writing — no refetch, no new
 * fetch — and names the first qualifying bucket plus a count of the rest.
 */
export function pendingWithRefsToastBody(rows: BucketRow[]): string | null {
  const flagged = rows.filter(
    (r) => !r.approved && (r.mod.trim() !== '' || r.fwa.trim() !== '' || r.approvedDate.trim() !== ''),
  );
  if (flagged.length === 0) return null;

  // Names whichever refs are actually present. A funded date carries no
  // human-readable value worth quoting on its own (unlike MOD/FWA, which are
  // the GC's own identifiers) — "a funded date" says what's on record without
  // implying the number itself is the interesting part.
  const refLabel = (r: BucketRow): string => {
    const mod = r.mod.trim();
    const fwa = r.fwa.trim();
    const date = r.approvedDate.trim();
    const parts: string[] = [];
    if (mod) parts.push(`MOD ${mod}`);
    if (fwa) parts.push(`FWA ${fwa}`);
    if (date) parts.push('a funded date');
    return parts.join(' / ');
  };
  const first = flagged[0]!;
  const firstIndex = rows.indexOf(first) + 1;
  const firstRef = refLabel(first);

  if (flagged.length === 1) {
    return `Bucket ${firstIndex} is Pending with ${firstRef} on record — flip it to Funded when the GC executes.`;
  }
  const restCount = flagged.length - 1;
  const bucketWord = restCount === 1 ? 'bucket' : 'buckets';
  const verb = restCount === 1 ? 'is' : 'are';
  return (
    `Bucket ${firstIndex} is Pending with ${firstRef} on record; ${restCount} more ${bucketWord} ` +
    `${verb} too — flip them to Funded when the GC executes.`
  );
}

export function EditPcoModal({
  open,
  onClose,
  onUpdated,
  onRefetch,
  initial,
  tranches,
  tranchesLoading,
}: {
  open: boolean;
  onClose: () => void;
  onUpdated: (pco: SettingsPco) => void | Promise<void>;
  /**
   * Refetch the page's PCO money + tranche list WITHOUT closing the modal.
   * Called after a partial-save failure so the editor's rows and the page's
   * figures reconcile with the DB before the user retries (prevents a
   * delete-already-deleted 404 loop on re-save).
   */
  onRefetch: () => void | Promise<void>;
  initial: EditPcoModalInitial | null;
  /** The PCO's funding tranches (owned + refetched by the parent page). */
  tranches: FundingTranche[];
  /** True while the tranche list is still loading — seed waits for it. */
  tranchesLoading: boolean;
}) {
  const { token, me } = useAuth();
  const toast = useToast();
  const [num, setNum] = useState('');
  const [desc, setDesc] = useState('');
  const [comments, setComments] = useState('');
  const [billingType, setBillingType] = useState<PcoBillingType | null>(null);
  const [isNoCost, setIsNoCost] = useState(false);
  const [isVoid, setIsVoid] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [rows, setRows] = useState<BucketRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Seed once per open, after tranches have loaded (spec Q2 seed rule). The
  // same effect re-seeds the rows (only) after a partial-save failure once the
  // parent has refetched — `reseedAfterErrorRef` makes it preserve the error
  // banner + the user's parent-field edits, and `reconcileTick` forces the
  // re-run on a render that already carries the fresh `tranches` prop.
  //
  // This effect and the adoption effect under it are ORDER-INDEPENDENT: both
  // gate on persistent refs, not on the other having already run this commit,
  // so neither can be "missed" by a render that carries both updates at once.
  const seededRef = useRef(false);
  const reseedAfterErrorRef = useRef(false);
  /**
   * The proposed figure the seed actually CONSUMED — what the save's
   * retirement is judged against, and deliberately not the live prop.
   *
   * The seed latches on the TRANCHES, which are a different payload from the
   * figure. So this ordering is real: seed with `undefined` (nothing migrated,
   * no residual row) → the settings row lands carrying a POSITIVE proposal →
   * the PM saves → reading the live prop would retire a figure that never
   * became a bucket. Judging against what was seeded makes that impossible in
   * every ordering; the adoption effect below is what lets the ordinary
   * retirement happen anyway once it is safe.
   */
  const seededProposedRef = useRef<number | null | undefined>(undefined);
  /**
   * Has anything touched the buckets since the seed? Only a PRISTINE list may
   * be rebuilt under the PM (adoption effect below). A save attempt counts as
   * touching: a partial failure leaves rows reflecting a half-applied write.
   */
  const rowsTouchedRef = useRef(false);
  const [reconcileTick, setReconcileTick] = useState(0);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      reseedAfterErrorRef.current = false;
      seededProposedRef.current = undefined;
      rowsTouchedRef.current = false;
      return;
    }
    if (!initial || seededRef.current || tranchesLoading) return;
    const preserveErr = reseedAfterErrorRef.current;
    if (!preserveErr) {
      setNum(initial.pco_number);
      setDesc(initial.description);
      setComments(initial.comments ?? '');
      setBillingType(initial.billing_type);
      setIsNoCost(initial.is_no_cost);
      setIsVoid(initial.status === 'void');
      setVoidReason(initial.void_reason ?? '');
      setErr(null);
    }
    // Error-reconcile pass: seed with proposed = null, i.e. derive NO
    // residual. A PUT-phase failure leaves amount_proposed_cents still
    // non-null while the residual is already a real (just-created) pending
    // tranche — deriving the residual again would double it and a retry would
    // persist phantom money. Nothing changes for a deduct: it never had a
    // residual to re-derive, and the retry's PUT still omits the field —
    // `seededProposedRef` deliberately keeps the value the FIRST seed
    // consumed, so this pass re-derives rows without re-deciding the
    // retirement.
    if (!preserveErr) seededProposedRef.current = initial.amount_proposed_cents;
    setRows(seedRows(tranches, preserveErr ? null : initial.amount_proposed_cents));
    setBusy(false);
    reseedAfterErrorRef.current = false;
    seededRef.current = true;
  }, [open, initial, tranches, tranchesLoading, reconcileTick]);

  // The proposed figure rides the SAME late payload as billing_type below, so
  // it too can land after the seed has latched — and unlike billing_type it is
  // money, so the fix has two halves. `seededProposedRef` (above) is the safe
  // one: a seed that ran without the figure omits it from the PUT forever, so
  // nothing can be destroyed whatever arrives later. This is the second half —
  // adopt it properly when that costs nothing, so the PCO doesn't sit unable
  // to retire its legacy figure for the rest of the session.
  //
  // Only while the buckets are PRISTINE. Re-seeding rebuilds the list, so a
  // dirty list would lose whatever the PM had typed; there the omission above
  // is the whole answer and the retirement simply waits for the next open.
  useEffect(() => {
    if (!open || !initial || !seededRef.current) return;
    if (seededProposedRef.current !== undefined) return;
    const loaded = initial.amount_proposed_cents;
    if (loaded === undefined || rowsTouchedRef.current) return;
    seededProposedRef.current = loaded;
    setRows(seedRows(tranches, loaded));
  }, [open, initial, tranches]);

  // billing_type arrives on a DIFFERENT payload than the tranches the seed
  // above waits for (the settings-PCO list), so it can land after the seed has
  // already latched `seededRef`. Without this the select would sit on
  // "Loading…" for the rest of the session and the field would stay omitted
  // from the PUT. Own effect, own guard: adopt the loaded value whenever it
  // appears, but never overwrite a choice the PM has already made.
  const billingTypeTouchedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      billingTypeTouchedRef.current = false;
      return;
    }
    if (billingTypeTouchedRef.current) return;
    const loaded = initial?.billing_type ?? null;
    if (loaded !== null) setBillingType(loaded);
  }, [open, initial]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open || !initial) return null;

  // Every bucket mutation marks the list dirty — that flag is what stops the
  // adoption effect above rebuilding rows the PM is in the middle of editing.
  const touchRows = () => {
    rowsTouchedRef.current = true;
  };
  const setRow = (key: string, patch: Partial<BucketRow>) => {
    touchRows();
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const addBucket = () => {
    touchRows();
    setRows((rs) => [...rs, blankPendingRow()]);
  };
  const removeRow = (key: string) => {
    touchRows();
    setRows((rs) => rs.filter((r) => r.key !== key));
  };

  const proposedSum = rows.reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);
  const approvedSum = rows
    .filter((r) => r.approved)
    .reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);
  const pendingSum = rows
    .filter((r) => !r.approved)
    .reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);

  const submit = async () => {
    if (!token) return;
    if (!num.trim() || !desc.trim()) {
      setErr('PCO number and description are required.');
      return;
    }
    for (const r of rows) {
      if (r.amountDollars.trim() !== '' && !Number.isFinite(Number(r.amountDollars))) {
        setErr('Bucket amounts must be numbers.');
        return;
      }
      // Checked in BOTH states — a pending bucket can carry a funded date now,
      // and the server validates the field regardless of the status flag.
      if (r.approvedDate.trim() && !DATE_RE.test(r.approvedDate.trim())) {
        setErr('Funded date must be YYYY-MM-DD.');
        return;
      }
    }

    setBusy(true);
    setErr(null);
    // A save attempt ends pristineness: past this point the rows can reflect a
    // partially-applied write, which is never something to rebuild from a
    // late-arriving figure.
    touchRows();
    try {
      const loadedById = new Map(tranches.map((t) => [t.id, t]));
      const keptIds = new Set(rows.map((r) => r.trancheId).filter((id): id is string => id !== null));

      // Ordering matters (atomicity, no unique constraint on funding_tranches):
      //   1. Creates + updates FIRST — a failure here never leaves an
      //      already-migrated PCO short its approved tranches.
      //   2. Deletes LAST — deletes-first buys nothing (no constraint) and a
      //      failure between them and the compensating creates would drop
      //      approved money permanently. Deletes-last makes any partial
      //      failure a transient over-count that self-heals on re-save.
      //   3. The PUT that retires amount_proposed_cents is STRICTLY last, so a
      //      partial failure never nulls proposed without the buckets landing.
      for (const r of rows) {
        const body = bodyForRow(r);
        if (r.trancheId === null) {
          if (!isBlankNewRow(r)) await api.pcos.createTranche(token, initial.id, body);
        } else {
          const orig = loadedById.get(r.trancheId);
          if (!orig || trancheChanged(orig, body)) {
            await api.pcos.updateTranche(token, r.trancheId, body);
          }
        }
      }
      for (const t of tranches) {
        if (!keptIds.has(t.id)) await api.pcos.deleteTranche(token, t.id);
      }

      // Void / un-void handling. paid + reopen stay on the header button, so
      // omit status otherwise to preserve the stored lifecycle value.
      let statusToSend: PcoStatus | undefined;
      if (isVoid) statusToSend = 'void';
      else if (initial.status === 'void') statusToSend = 'open';

      // Retire the legacy proposed field for THIS PCO — pending now lives in
      // the buckets, so nulling it prevents the derivation double-counting.
      // The helper is what decides, because the retirement is only safe where
      // the seed migrated the figure into a bucket: a DEDUCT never can be (the
      // residual is clamped at 0), and a figure the seed never read was never
      // migrated either. In both cases the field is omitted and the server
      // keeps it. The argument is the SEEDED figure, never the live prop —
      // that prop can change under an open modal (see `seededProposedRef`).
      const body: PcoWriteRequest = {
        pco_number: num.trim(),
        description: desc.trim(),
        ...proposedRetirementPatch(seededProposedRef.current),
        comments: comments.trim() || null,
        void_reason: isVoid ? voidReason.trim() || null : null,
        is_no_cost: isNoCost,
        ...(statusToSend ? { status: statusToSend } : {}),
        // Omitted when it never loaded — the server then preserves the stored
        // type instead of taking a default as an instruction.
        ...(billingType ? { billing_type: billingType } : {}),
      };
      const updated = await api.settings.updatePco(token, initial.id, body);
      // Pending-with-refs nudge (the "064 silence" fix) — reads the rows this
      // save just wrote, not a refetch. Silent when nothing qualifies.
      const pendingToastBody = pendingWithRefsToastBody(rows);
      if (pendingToastBody) {
        toast.push({ variant: 'success', title: 'Saved', body: pendingToastBody, durationMs: 6000 });
      }
      await onUpdated(updated);
    } catch (e) {
      const reason = e instanceof Error && e.message ? ` (${e.message})` : '';
      setErr(
        `Save didn't finish — some changes may have been applied. The buckets ` +
          `below have been refreshed from the server; please review and save ` +
          `again.${reason}`,
      );
      // Reconcile the editor + page money with the DB, then re-seed the rows
      // so a retry never re-deletes an already-deleted bucket (404 loop). Set
      // the reseed flags only AFTER the refetch lands so the re-seed reads the
      // fresh `tranches` prop, not the stale pre-save one.
      try {
        await onRefetch();
      } catch {
        // best-effort — the error copy already warns the save was partial.
      }
      reseedAfterErrorRef.current = true;
      seededRef.current = false;
      setReconcileTick((t) => t + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Edit PCO"
    >
      <div className="new-pco-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Edit PCO {formatPcoNumber(initial.pco_number)}</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">PCO details</span>
            </div>
            <div className="pco-card-body">
              <div className="new-pco-row">
                <div className="new-pco-field">
                  <label className="field-label">PCO #</label>
                  <input
                    className="field-input mono"
                    value={num}
                    onChange={(e) => setNum(e.target.value)}
                    autoFocus
                    disabled={busy}
                  />
                </div>
                <div className="new-pco-field">
                  <label className="field-label">Description</label>
                  <input
                    className="field-input"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    disabled={busy}
                  />
                </div>
              </div>

              {/* Billing method — the fix-a-misfile control. Picked at
                  creation from the New PCO chooser; this is where a wrong
                  pick gets corrected. Organizational only: flipping it
                  changes which lists show the PCO, never a dollar, a
                  rollup, or a total. */}
              <div className="new-pco-field">
                <span className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  Billing method
                  <HelpTip title="T&amp;M Weekly Rollup vs Lump-Sum" learnMore="pcos-and-funding">
                    <strong>T&amp;M Weekly Rollup</strong> bills week by week in the
                    portal off foreman PDFs. <strong>Lump-Sum</strong> is priced
                    outside and just tracked here on the PCO Log. Only weekly-rollup
                    PCOs appear in the side rail's Recent list. Changing this moves
                    no money.
                  </HelpTip>
                </span>
                <Dropdown
                  style={{ width: '100%' }}
                  options={BILLING_TYPE_OPTIONS}
                  value={billingType ?? ''}
                  placeholder="Loading…"
                  onChange={(next) => {
                    billingTypeTouchedRef.current = true;
                    setBillingType(next as PcoBillingType);
                  }}
                  disabled={busy || billingType === null}
                  aria-label="Billing method"
                />
              </div>
            </div>
          </div>

          {/* ===== Funding buckets ===== */}
          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">
                Funding buckets
                <HelpTip title="What a funding bucket is" learnMore="pcos-and-funding">
                  Each bucket = one {gcName(me)} approval (MOD). The totals strip
                  below shows their sum. A bucket is <strong>Pending</strong> until
                  {' '}{gcName(me)} executes it — then flip it to{' '}
                  <strong>Funded</strong>. Fill in the MOD / FWA / date whenever
                  you have them; they're kept in either state, so a bucket can sit
                  Pending with its references already known.
                </HelpTip>
              </span>
            </div>
            <div className="pco-card-body">
              {/* No inline explainer here — the HelpTip above says the same
                  thing, and standing copy the PM has already read is noise on
                  every visit. Same de-duplication as the create form. */}
              {tranchesLoading && rows.length === 0 ? (
                <p className="muted" style={{ fontSize: 13 }}>Loading buckets…</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {rows.map((r, i) => (
                    <BucketRowEditor
                      key={r.key}
                      row={r}
                      index={i}
                      busy={busy}
                      onChange={(patch) => setRow(r.key, patch)}
                      onRemove={() => removeRow(r.key)}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="pco-card-foot">
              <button type="button" className="btn btn-ghost btn-compact" onClick={addBucket} disabled={busy}>
                + Add bucket
              </button>
              <div
                className="mono"
                style={{ fontSize: 12.5, color: 'var(--ink-2)', fontVariantNumeric: 'tabular-nums' }}
              >
                Proposed {formatMoneyCents(proposedSum)}
                {'  ·  '}Approved {formatMoneyCents(approvedSum)}
                {'  ·  '}Pending {formatMoneyCents(pendingSum)}
              </div>
              {!isNoCost && proposedSum === 0 && rows.length > 0 && (
                <p style={{ flexBasis: '100%', margin: 0, fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.4 }}>
                  No bucket has an amount yet — you can still save, but this PCO
                  won't show a proposed or approved figure.
                </p>
              )}
            </div>
          </div>

          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">Comments</span>
            </div>
            <div className="pco-card-body">
              <input
                className="field-input"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={busy}
                placeholder="optional — e.g. 'Awaiting LS MOD'"
              />
            </div>
          </div>

          {/* ===== Status: no-cost + void (paid/reopen live on the header
              button) ===== */}
          <div className="pco-card">
            <div className="pco-card-head">
              <span className="pco-card-title">Status</span>
            </div>
            <div className="pco-card-body">
              <div className="new-pco-field">
                <span className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Toggle
                    value={isNoCost}
                    onChange={setIsNoCost}
                    disabled={busy}
                    aria-label="No cost — placeholder PCO that won't bill"
                  />
                  No cost — placeholder PCO that won't bill
                </span>
              </div>

              <div className="new-pco-field">
                <span className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Toggle
                    value={isVoid}
                    onChange={setIsVoid}
                    disabled={busy}
                    aria-label="Void this PCO"
                  />
                  Void this PCO
                </span>
                {isVoid && (
                  <input
                    className="field-input"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    disabled={busy}
                    placeholder="Void reason — e.g. 'Work not happening' or 'Washed with IFC Site Recon'"
                    style={{ marginTop: 6 }}
                  />
                )}
              </div>
            </div>
          </div>

          {err && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</div>}
          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-cta" onClick={submit} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
